import { z } from 'zod';
import type { IsoDate } from './common.js';
import type { UserSummary } from './users.js';

/** Discord-compatible embed (subset). Rendered as a card under the message. */
export const embedSchema = z.object({
  title: z.string().max(256).optional(),
  description: z.string().max(4096).optional(),
  url: z.url().max(2000).optional(),
  /** RGB colour as an integer (Discord style), e.g. 0x5865f2. */
  color: z.number().int().min(0).max(0xffffff).optional(),
  author: z.object({ name: z.string().max(256), url: z.url().optional() }).optional(),
  fields: z
    .array(
      z.object({
        name: z.string().max(256),
        value: z.string().max(1024),
        inline: z.boolean().optional(),
      }),
    )
    .max(25)
    .optional(),
  image: z.object({ url: z.url() }).optional(),
  thumbnail: z.object({ url: z.url() }).optional(),
  footer: z.object({ text: z.string().max(2048) }).optional(),
  timestamp: z.iso.datetime({ offset: true }).optional(),
});
export type MessageEmbed = z.infer<typeof embedSchema>;

/**
 * Body accepted by `POST /api/webhooks/:id/:token`. Compatible with Discord webhooks
 * (`content`, `username`, `avatar_url`, `embeds`) and Slack's `text`.
 */
export const executeWebhookSchema = z
  .object({
    content: z.string().max(4000).optional(),
    text: z.string().max(4000).optional(),
    username: z.string().trim().min(1).max(80).optional(),
    avatar_url: z.url().max(2000).optional(),
    embeds: z.array(embedSchema).max(10).optional(),
  })
  .refine((body) => Boolean(body.content?.trim() || body.text?.trim() || body.embeds?.length), {
    message: 'Se necesita content, text o embeds',
  });
export type ExecuteWebhookInput = z.infer<typeof executeWebhookSchema>;

export const createWebhookSchema = z.object({
  name: z.string().trim().min(1, 'Obligatorio').max(80),
  avatarUrl: z.url().max(2000).nullable().optional(),
});
export type CreateWebhookInput = z.infer<typeof createWebhookSchema>;

export interface ChannelWebhook {
  id: string;
  channelId: string;
  name: string;
  avatarUrl: string | null;
  createdBy: UserSummary | null;
  createdAt: IsoDate;
  lastUsedAt: IsoDate | null;
}

/** Returned only when the webhook is created: the URL contains the secret token. */
export interface CreatedWebhook extends ChannelWebhook {
  url: string;
}
